import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Lock, EyeOff, ExternalLink } from "lucide-react";

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-[#0b1120] text-gray-100 font-sans selection:bg-[#22d3ee] selection:text-black">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-white/10 bg-[#0b1120]/80 backdrop-blur-md px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-400 hover:text-white transition-colors">
            <ArrowLeft size={18} />
            <span>Back to ReactionCam</span>
          </Link>
          <div className="flex items-center gap-4 text-xs font-mono uppercase tracking-wider text-gray-400">
            <Link to="/terms" className="hover:text-white">Terms of Use</Link>
            <span>·</span>
            <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-white flex items-center gap-1">
              Corvas Studio <ExternalLink size={12} />
            </a>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-6 py-12 space-y-8">
        <section className="text-center py-6">
          <p className="text-xs font-mono tracking-widest uppercase text-[#22d3ee] mb-2">Legal &amp; Privacy Transparency</p>
          <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
            Privacy Policy
          </h1>
          <p className="text-sm text-gray-400">
            Application: <strong>Reaction Maker - ReactionCam</strong> (<code>com.corvas.reactioncam</code>)<br />
            Effective Date: March 1, 2026 · Developer: <strong>Corvas</strong>
          </p>
        </section>

        {/* Highlights */}
        <div className="grid sm:grid-cols-3 gap-4">
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
            <div className="flex items-center gap-2 text-[#22d3ee] font-semibold text-sm mb-1">
              <ShieldCheck size={18} />
              <span>100% On-Device</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              Active recordings, gameplay, and media generation run on local device hardware without server uploads.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
            <div className="flex items-center gap-2 text-[#22d3ee] font-semibold text-sm mb-1">
              <Lock size={18} />
              <span>Zero Account Tracking</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              No login accounts, passwords, or personal profiles are required or collected by our team.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-[#111827] p-5">
            <div className="flex items-center gap-2 text-[#22d3ee] font-semibold text-sm mb-1">
              <EyeOff size={18} />
              <span>Safe Third Parties</span>
            </div>
            <p className="text-xs text-gray-400 leading-relaxed">
              We rely strictly on Google Play Services, AdMob, and Firebase for runtime stability and licensing.
            </p>
          </div>
        </div>

        {/* Section 1 */}
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">1. Overview &amp; Introduction</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            This Privacy Policy applies to the mobile software application <strong>Reaction Maker - ReactionCam</strong> (the "Application"), developed and distributed by <strong>Corvas</strong> ("we," "our," or "Service Provider").
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            We are committed to building high-performance, privacy-respecting software. This document discloses exactly what data the app interacts with, device permissions requested, and third-party services utilized.
          </p>
        </section>

        {/* Section 2 */}
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">2. Data Architecture &amp; Processing Boundaries</h2>
          
          <div>
            <h3 className="text-base font-semibold text-[#22d3ee] mb-1">A. Data Collected Directly by Developer</h3>
            <p className="text-sm text-gray-300 leading-relaxed">None. We do not require accounts, logins, email addresses, or personal profiles to use ReactionCam.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#22d3ee] mb-1">B. Data Processed Strictly Locally (Never Uploaded)</h3>
            <p className="text-sm text-gray-300 leading-relaxed">All video recording, audio synchronization, camera overlay positioning, layout composition, trimming, and video file exports are executed 100% locally on your device hardware.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#22d3ee] mb-1">C. Data Transmitted to Our Backend</h3>
            <p className="text-sm text-gray-300 leading-relaxed">None. The app operates without uploading your videos, audio, or metadata to our servers.</p>
          </div>

          <div className="pt-2">
            <h3 className="text-base font-semibold text-[#22d3ee] mb-1">D. Diagnostic Telemetry Collected by Third Parties</h3>
            <p className="text-sm text-gray-300 leading-relaxed">
              Standard telemetry (anonymous crash logs, GPU capabilities, operating system version, and general ad impressions) is collected automatically by Google Play Services and AdMob according to their strict privacy standards.
            </p>
          </div>
        </section>

        {/* Permissions */}
        
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">3. Device Permissions &amp; Hardware Access</h2>
          <p className="text-sm text-gray-300">
            To provide core features, ReactionCam may request the following device permissions:
          </p>
          <div className="space-y-3 pt-2">
            
            <div className="rounded-xl border border-white/10 bg-black/30 p-4" key="Camera Access">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-semibold text-white text-base">Camera Access</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-[#22d3ee]">Runtime User Permission</span>
              </div>
              <p className="text-sm text-gray-300 mb-1"><strong>Purpose:</strong> Captures front-facing video overlay (facecam) while you record your live reaction to on-screen or imported content.</p>
              <p className="text-sm text-gray-400"><strong>Data Handling:</strong> Processed entirely on-device in real-time. Video streams are rendered directly into local video files and are never streamed or uploaded to any remote server.</p>
            </div>
  

            <div className="rounded-xl border border-white/10 bg-black/30 p-4" key="Microphone Access">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-semibold text-white text-base">Microphone Access</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-[#22d3ee]">Runtime User Permission</span>
              </div>
              <p className="text-sm text-gray-300 mb-1"><strong>Purpose:</strong> Captures your voice commentary and live audio during reaction recordings.</p>
              <p className="text-sm text-gray-400"><strong>Data Handling:</strong> Processed strictly on-device and multiplexed directly into your output video. Audio is never transmitted externally.</p>
            </div>
  

            <div className="rounded-xl border border-white/10 bg-black/30 p-4" key="Screen Capture (MediaProjection)">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-semibold text-white text-base">Screen Capture (MediaProjection)</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-[#22d3ee]">Explicit User Consent Screen-Cast Prompt</span>
              </div>
              <p className="text-sm text-gray-300 mb-1"><strong>Purpose:</strong> Enables recording of device screen activity when creating reaction content over games, browsers, or other apps.</p>
              <p className="text-sm text-gray-400"><strong>Data Handling:</strong> Local system video frames are captured only while recording is actively initiated by the user, and saved directly to device storage.</p>
            </div>
  

            <div className="rounded-xl border border-white/10 bg-black/30 p-4" key="Photos &amp; Media Storage">
              <div className="flex flex-wrap items-baseline justify-between gap-2 mb-2">
                <span className="font-semibold text-white text-base">Photos &amp; Media Storage</span>
                <span className="text-xs font-mono px-2 py-0.5 rounded bg-white/5 text-[#22d3ee]">Storage / Media Library Access</span>
              </div>
              <p className="text-sm text-gray-300 mb-1"><strong>Purpose:</strong> Allows importing background/source videos to react to, and saving completed reaction videos to your device gallery.</p>
              <p className="text-sm text-gray-400"><strong>Data Handling:</strong> Strictly local. All imported and exported media stays in your local device file system.</p>
            </div>
  
          </div>
        </section>
        

        {/* Third-Party SDKs */}
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">4. Third-Party SDKs &amp; Service Providers</h2>
          <p className="text-sm text-gray-300">
            The Application integrates official third-party SDKs to support app distribution, licensing, analytics, and non-intrusive advertisements:
          </p>
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="w-full text-left text-sm">
              <thead className="bg-black/40 text-xs font-semibold uppercase text-gray-400">
                <tr>
                  <th className="p-3">Service</th>
                  <th className="p-3">Purpose</th>
                  <th className="p-3">Privacy Link</th>
                </tr>
              </thead>
              <tbody>
                
                <tr className="border-b border-white/5" key="Google Play Services">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google Play Services</td>
                  <td className="p-3 text-gray-300 text-sm">App delivery, licensing verification, and core runtime services.</td>
                  <td className="p-3 text-sm">
                    <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22d3ee] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  

                <tr className="border-b border-white/5" key="Google AdMob">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google AdMob</td>
                  <td className="p-3 text-gray-300 text-sm">Displays banner, interstitial, and rewarded advertisements in the free tier of the application.</td>
                  <td className="p-3 text-sm">
                    <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-[#22d3ee] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  

                <tr className="border-b border-white/5" key="Firebase Analytics &amp; Crashlytics">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Firebase Analytics &amp; Crashlytics</td>
                  <td className="p-3 text-gray-300 text-sm">Collects anonymous device technical telemetry (device model, OS version, frame render rates, crash logs) to diagnose errors and optimize rendering stability.</td>
                  <td className="p-3 text-sm">
                    <a href="https://firebase.google.com/support/privacy" target="_blank" rel="noopener noreferrer" className="text-[#22d3ee] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  

                <tr className="border-b border-white/5" key="Google Play In-App Billing">
                  <td className="p-3 font-semibold text-white whitespace-nowrap">Google Play In-App Billing</td>
                  <td className="p-3 text-gray-300 text-sm">Processes optional Pro upgrades (watermark removal, unlimited recording length, HD export). Payment details are securely processed directly by Google Play and are never seen or stored by Corvas.</td>
                  <td className="p-3 text-sm">
                    <a href="https://payments.google.com/payments/apis-secure/get_legal_document?ldo=0&amp;ldt=privacynotice" target="_blank" rel="noopener noreferrer" className="text-[#22d3ee] hover:underline font-mono text-xs">
                      View Policy ↗
                    </a>
                  </td>
                </tr>
  
              </tbody>
            </table>
          </div>
        </section>

        {/* Disclaimers */}
        
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">5. Product-Specific Clarifications &amp; Disclaimers</h2>
          <div className="space-y-4 pt-2">
            
          <div className="border-t border-white/5 pt-4 first:border-none first:pt-0" key="Third-Party Content &amp; Intellectual Property">
            <h4 className="text-base font-semibold text-[#22d3ee] mb-1">Third-Party Content &amp; Intellectual Property</h4>
            <p className="text-sm text-gray-300 leading-relaxed">ReactionCam is a recording utility. Users are solely responsible for ensuring they possess all necessary rights, licenses, or fair-use justifications for any third-party videos, music, games, or media they record or react to. Corvas does not monitor or endorse user-generated videos.</p>
          </div>
  

          <div className="border-t border-white/5 pt-4 first:border-none first:pt-0" key="Platform Non-Affiliation">
            <h4 className="text-base font-semibold text-[#22d3ee] mb-1">Platform Non-Affiliation</h4>
            <p className="text-sm text-gray-300 leading-relaxed">ReactionCam is an independent production tool developed by Corvas. It is not affiliated with, sponsored by, or endorsed by YouTube, TikTok, Instagram, Twitch, Meta, or any social media platform.</p>
          </div>
  
          </div>
        </section>
        

        {/* Data Retention & Rights */}
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-4">
          <h2 className="text-xl font-bold text-white">6. Data Retention, User Control &amp; Children's Privacy</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            All user preferences and files reside strictly on your local device. You may purge data at any time via <em>Android Settings &gt; Apps &gt; ReactionCam &gt; Clear Storage</em>, or by uninstalling the application.
          </p>
          <p className="text-sm text-gray-300 leading-relaxed">
            We do not knowingly market to or solicit data from children under 13.
          </p>
        </section>

        {/* Contact */}
        <section className="rounded-2xl border border-white/10 bg-[#111827] p-6 sm:p-8 space-y-3">
          <h2 className="text-xl font-bold text-white">7. Contact Information</h2>
          <p className="text-sm text-gray-300 leading-relaxed">
            If you have questions or concerns regarding this Privacy Policy, please contact us directly:
          </p>
          <p className="text-sm text-gray-300">
            <strong>Developer:</strong> Corvas<br />
            <strong>Email:</strong> <a href="mailto:corvas.official@gmail.com" className="text-[#22d3ee] hover:underline">corvas.official@gmail.com</a><br />
            <strong>Studio:</strong> <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="text-[#22d3ee] hover:underline">https://studio.corvas.in</a>
          </p>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8 text-center text-xs text-gray-500">
        <div className="flex justify-center gap-4 mb-2">
          <Link to="/" className="hover:text-gray-300">Home</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-gray-300">Terms of Use</Link>
          <span>·</span>
          <a href="https://studio.corvas.in" target="_blank" rel="noopener noreferrer" className="hover:text-gray-300">Corvas Studio</a>
        </div>
        <p>&copy; {new Date().getFullYear()} Corvas. All rights reserved.</p>
      </footer>
    </div>
  );
}
